import React, { Fragment, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import Countdown from "react-countdown";
import {
  getCartExpiresAt,
  getCartTimeoutMinutes,
} from "../../../../utils/cartExpiry";

/**
 * The cart hold countdown.
 *
 * The deadline comes from the server: each cart item carries an `expired`
 * timestamp derived from SiteSetting.carttimeout, and adding to the cart pushes
 * it out for every item at once. This component no longer keeps a clock of its
 * own -- it used to compute one from `lastAddedBookToCart` plus a hardcoded
 * CART_TIMEOUT, which drifted away from both the server's abandonment deadline
 * and the localStorage purge in utils/ExpiredCart.js.
 */
const Clock = ({ refCallback, onCompleteTimer, cart, start, stop, lastAddedBookToCart }) => {
  const { setting } = useSelector((state) => state.setting);
  const timeoutMinutes = getCartTimeoutMinutes(setting);

  const expiresAt = useMemo(
    () => getCartExpiresAt(cart, lastAddedBookToCart, timeoutMinutes),
    [cart, lastAddedBookToCart, timeoutMinutes]
  );

  const hasItems = Array.isArray(cart) && cart.length > 0;

  // Keep the parent's start/stop controls working without making them effect
  // dependencies -- they are redefined on every render of the parent.
  const controls = useRef({ start, stop });
  controls.current = { start, stop };

  useEffect(() => {
    try {
      if (hasItems) {
        controls.current.start();
      } else {
        controls.current.stop();
      }
    } catch {
      /* the countdown ref is not attached yet; autoStart covers the first render */
    }
    // Re-run when the deadline moves (an item was added) or the cart empties.
  }, [hasItems, expiresAt]);

  const pad = (value) => (value < 10 ? `0${value}` : `${value}`);

  const renderer = ({ hours, minutes, seconds, completed }) => {
    if (completed) return <span className="noto">00:00:00</span>;

    return (
      <span className="noto">
        {pad(hours)}:{pad(minutes)}:{pad(seconds)}
      </span>
    );
  };

  // Empty cart: show the full hold, frozen, rather than collapsing the row. This
  // is what the old component displayed when it started with autoStart off.
  if (expiresAt === null) {
    const hours = Math.floor(timeoutMinutes / 60);
    return (
      <span className="noto">
        {pad(hours)}:{pad(timeoutMinutes % 60)}:00
      </span>
    );
  }

  return (
    <Fragment>
      <Countdown
        // Remount when the deadline moves; react-countdown latches `date` on mount.
        key={expiresAt.getTime()}
        ref={refCallback}
        date={expiresAt}
        intervalDelay={1000}
        zeroPadTime={2}
        autoStart={hasItems}
        daysInHours
        renderer={renderer}
        onComplete={onCompleteTimer}
      />
    </Fragment>
  );
};

export default Clock;
